import { SpaceSvg } from '@assets/custom_icons/components/Space';
import SidebarItem from '@scenes/Home/components/SidebarItem';
import { Icon, Layout, Menu } from 'antd';
import { ClickParam } from 'antd/lib/menu';
import SubMenu from 'antd/lib/menu/SubMenu';
import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { WebhookSvg } from '../../assets/custom_icons/components/webhook';
import {
    apiKeysPath, baseRepositoriesPath,
    contentDefinitionBasePath,
    contentsBasePath, createUsersPath,
    createWebhooksListPath, dashboardPath, extensionsPath, mediaGalleryBasePath,
    publishingChannelBasePath,
    publishToLiveBasePath, spaceSettingsPath
} from '../../constants/routing-constants';
import { userPermissionsTable } from '../../constants/user-permissions-table';
import styles from './SideBar.module.scss';
import { SidebarItems, SideBarProps, SideBarState } from './SideBarModel';

const sidebarTitles = defineMessages({
    home: {
        id: 'sideBar.home',
        defaultMessage: 'My Dashboard'
    },
    repositories: {
        id: 'sideBar.repositories',
        defaultMessage: 'Repositories'
    },
    contents: {
        id: 'sideBar.contents',
        defaultMessage: 'Contents'
    },
    mediaGalleries: {
        id: 'sideBar.mediaGalleries',
        defaultMessage: 'Media Galleries'
    },
    contentDefinitions: {
        id: 'sideBar.contentDefinitions',
        defaultMessage: 'Definitions'
    },
    publishingChannels: {
        id: 'sideBar.publishingChannels',
        defaultMessage: 'Channels'
    },
    publish: {
        id: 'sideBar.publishToLive',
        defaultMessage: 'Publish to live'
    },
    settings: {
        id: 'sideBar.settings',
        defaultMessage: 'Settings'
    },
    usersAndPermissions: {
        id: 'sideBar.usersAndPermissions',
        defaultMessage: 'Users'
    },
    webhooks: {
        id: 'sideBar.webhooks',
        defaultMessage: 'Webhooks'
    },
    spaceKeys: {
        id: 'sideBar.spaceKeys',
        defaultMessage: 'API keys'
    },
    advanced: {
        id: 'sideBar.advanced',
        defaultMessage: 'Advanced'
    },
    extensions: {
        id: 'sideBar.extensions',
        defaultMessage: 'Extensions'
    },
    spaceSettings: {
        id: 'sideBar.spaceSettings',
        defaultMessage: 'Space Settings'
    }
});

const { Sider } = Layout;

class SideBar extends Component<SideBarProps, SideBarState> {

    constructor(props: SideBarProps) {
        super(props);
        this.state = {
            activeKey: this.findActiveMenuLink()
        };
    }

    findActiveMenuLink = () => {
        const paths = this.props.location.pathname.split('/');
        const mainSection = paths[3];

        if (mainSection === '') {
            return SidebarItems.DASHBOARD as SidebarItems;
        }

        const firstLevelKeys = [SidebarItems.CONTENTS, SidebarItems.CONTENT_DEFINITIONS, SidebarItems.MEDIA_GALLERIES,
        SidebarItems.PUBLISH_TO_LIVE, SidebarItems.REPOSITORIES,
        SidebarItems.PUBLISHING_CHANNELS,
        SidebarItems.USERS_AND_PERMISSIONS, SidebarItems.WEBHOOKS, SidebarItems.SpaceKeys, SidebarItems.EXTENSIONS,
        SidebarItems.SPACE_SETTINGS];

        if (firstLevelKeys.indexOf(mainSection as SidebarItems) >= 0) {
            return mainSection as SidebarItems;
        }

        return this.state.activeKey;

    }

    onClick = (e: ClickParam) => {
        this.setState({
            activeKey: e.key as SidebarItems
        });
    }

    render() {
        const { formatMessage } = this.props.intl;
        const { match: { params: { spaceId } } } = this.props;

        return (
            <Sider
                className={styles.SideBar}
                breakpoint="md"
                collapsedWidth={64}
            >
                <Menu
                    className={styles.MenuContainer}
                    mode="vertical"
                    onClick={this.onClick}
                    selectedKeys={[this.state.activeKey]}
                    theme="dark"
                >
                    <SidebarItem
                        key={SidebarItems.DASHBOARD}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="home"
                        title={formatMessage(sidebarTitles.home)}
                        to={dashboardPath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.CONTENTS}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="database"
                        title={formatMessage(sidebarTitles.contents)}
                        to={contentsBasePath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.REPOSITORIES}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="appstore"
                        title={formatMessage(sidebarTitles.repositories)}
                        to={baseRepositoriesPath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.CONTENT_DEFINITIONS}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="book"
                        title={formatMessage(sidebarTitles.contentDefinitions)}
                        to={contentDefinitionBasePath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.MEDIA_GALLERIES}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="picture"
                        title={formatMessage(sidebarTitles.mediaGalleries)}
                        to={mediaGalleryBasePath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.PUBLISH_TO_LIVE}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="cloud-upload"
                        title={formatMessage(sidebarTitles.publish)}
                        to={publishToLiveBasePath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.PUBLISHING_CHANNELS}
                        permissions={userPermissionsTable.SideBar.others}
                        className={styles.SideBarItemDefault}
                        icon="cluster"
                        title={formatMessage(sidebarTitles.publishingChannels)}
                        to={publishingChannelBasePath(spaceId)}
                    />
                    <SidebarItem
                        key={SidebarItems.USERS_AND_PERMISSIONS}
                        permissions={userPermissionsTable.SideBar.usersAndPermissions}
                        className={styles.SideBarItemDefault}
                        icon="team"
                        title={formatMessage(sidebarTitles.usersAndPermissions)}
                        to={createUsersPath(spaceId)}
                    />
                    <SubMenu
                        key="advanced"
                        title={
                            <React.Fragment>
                                <Icon type="sliders" /> <span>{formatMessage(sidebarTitles.advanced)}</span>
                            </React.Fragment>
                        }
                        popupOffset={[25, 0]}
                    >
                        <SidebarItem
                            key={SidebarItems.WEBHOOKS}
                            permissions={userPermissionsTable.SideBar.webhooks}
                            className={styles.SideBarItemDefault}
                            iconComponent={WebhookSvg}
                            title={formatMessage(sidebarTitles.webhooks)}
                            to={createWebhooksListPath(spaceId)}
                        />
                        <SidebarItem
                            key={SidebarItems.SpaceKeys}
                            permissions={userPermissionsTable.SideBar.others}
                            className={styles.SideBarItemDefault}
                            icon="key"
                            title={formatMessage(sidebarTitles.spaceKeys)}
                            to={apiKeysPath(spaceId)}
                        />
                        <SidebarItem
                            key={SidebarItems.EXTENSIONS}
                            permissions={userPermissionsTable.SideBar.others}
                            className={styles.SideBarItemDefault}
                            icon="layout"
                            title={formatMessage(sidebarTitles.extensions)}
                            to={extensionsPath(spaceId)}
                        />
                        <SidebarItem
                            key={SidebarItems.SPACE_SETTINGS}
                            permissions={userPermissionsTable.SideBar.usersAndPermissions}
                            className={styles.SideBarItemDefault}
                            iconComponent={SpaceSvg}
                            title={formatMessage(sidebarTitles.spaceSettings)}
                            to={spaceSettingsPath(spaceId)}
                        />
                    </SubMenu>
                </Menu>
            </Sider>
        );
    }
}

export default withRouter(injectIntl(SideBar));
